import { type KeyboardEvent, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { useSearchParameters } from '@/components/signup/hooks/use-search-parameters';
import { ufoEventConfirmationPageTemplateSelectionContinue } from '@/components/signup/utils/ufo-events';
import { useUserContext } from '@/components/user-context/user-context-provider';
import { DataFoundationsWrapper } from '@/utils/data-foundations/data-foundation-types';

import { DwellTimeWrapper } from '../../components/dwell-time-wrapper/dwell-time-wrapper';
import { useConfirmationPage } from '../../hooks/use-confirmation-page';
import { useGupsStatus } from '../../hooks/use-gups';
import { useJiraTemplateChoice } from '../../hooks/use-jira-template-choice';
import { useScreenManager } from '../../hooks/use-screen-manager';
import { getPrimaryProduct } from '../../hooks/utils/use-cloud-status-utils';
import { USER_SEGMENTATION_TEAM_TYPE_SOFTWARE } from '../../models/user-segmentation';
import { getCurrentUrlWithParams } from '../../utils/utils';
import { JiraOnboardingContainer } from '../jira/jira-onboarding-container/jira-onboarding-container';
import { TRACKING_EVENT_ACTION_SUBJECT_IDS } from '../user-seg-bxp-ui/constants';

import { WorkStyleChoiceCard } from './work-style-choice-card';
import { WorkStyleChoiceLoadingState } from './work-style-choice-loading-state';
import { workStyleChoiceMessages } from './work-style-choice-messages';
import { getWorkStyleChoicePlan } from './work-style-choice-options';

import type { TemplateOption } from '../../components/template-choice/template-definitions';

type WorkStyleChoiceSelection = 'chosen' | 'skipped';

const RADIO_KEY_STEPS: Partial<Record<string, number>> = {
	ArrowRight: 1,
	ArrowDown: 1,
	ArrowLeft: -1,
	ArrowUp: -1,
};

const cardsStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.300'),
	width: '100%',
	margin: 0,
	padding: 0,
	border: 'none',
	alignItems: 'center',
	[media.above.md]: {
		flexDirection: 'row',
		alignItems: 'stretch',
		justifyContent: 'center',
	},
});

const continueStyles = css({
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'center',
	margin: 0,
	marginBlockStart: token('space.500'),
	padding: 0,
	border: 'none',
	gap: token('space.100'),
	width: '100%',
	[media.below.sm]: {
		position: 'fixed',
		insetInlineStart: 0,
		insetBlockEnd: '0',
		backgroundColor: token('elevation.surface'),
		padding: '14px 18px',
		width: '100%',
		zIndex: 10,
		boxShadow: token('elevation.shadow.overflow'),
	},
});

const continueButtonStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		borderRadius: '6px !important',
		paddingBlock: '6px !important',
		paddingInline: '12px !important',
		margin: '0 !important',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	span: {
		fontFamily: `var(--atlassian-sans-font-family) !important`,
		fontSize: '14px !important',
		lineHeight: '1.5rem !important',
		fontWeight: `${token('font.weight.medium')} !important`,
		textAlign: 'center',
		margin: '0 !important',
	},
});

const backButtonStyles = css({
	outline: `1px solid ${token('color.border')}`,
	outlineOffset: '-1px',
	borderRadius: '6px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		borderRadius: '6px !important',
		paddingBlock: '6px !important',
		paddingInline: '12px !important',
		margin: '0 !important',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	span: {
		color: `${token('color.text')} !important`,
		fontFamily: `var(--atlassian-sans-font-family) !important`,
		fontSize: '14px !important',
		lineHeight: '1.5rem !important',
		fontWeight: `${token('font.weight.medium')} !important`,
		textAlign: 'center',
		margin: '0 !important',
	},
});

export const WorkStyleChoiceView = () => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { addTemplateCallback } = useGupsStatus();
	const { nextScreen, prevScreen, prevScreenExists } = useScreenManager();
	const {
		setTemplateChoice,
		setTemplateChoiceId,
		setTemplateChoiceName,
		setTemplateMetadata,
		goBackToUserSegmentation,
	} = useJiraTemplateChoice();
	const {
		userSegmentationContext: { selectedTeamType: rawSelectedTeamType },
	} = useConfirmationPage();
	const teamType = rawSelectedTeamType ?? USER_SEGMENTATION_TEAM_TYPE_SOFTWARE;

	const { products, edition } = useSearchParameters();
	const { profile } = useUserContext();
	const dataFoundationsAttributes = useMemo(
		() => ({
			productKey: getPrimaryProduct(products),
			edition,
			teamType,
			userType: profile.cohorts?.userType,
			domainType: profile.cohorts?.domainType,
			companyType: profile.cohorts?.companyType,
		}),
		[products, edition, teamType, profile.cohorts],
	);

	useLayoutEffect(() => {
		if (!rawSelectedTeamType) {
			return;
		}
		const url = new URL(window.location.href);
		if (!url.searchParams.get('team_type')) {
			url.searchParams.set('team_type', rawSelectedTeamType);
			history.replaceState(null, '', url.toString());
		}
	}, [rawSelectedTeamType]);

	const plan = useMemo(
		() => getWorkStyleChoicePlan(teamType, intl.formatMessage),
		[teamType, intl.formatMessage],
	);

	const [selectedTemplateId, setSelectedTemplateId] = useState<string>();

	const hasCommittedChoice = useRef(false);
	const [isSaving, setIsSaving] = useState(false);

	const saveTemplate = useCallback(
		async (
			{ templateId, templateType }: TemplateOption['option'],
			selection: WorkStyleChoiceSelection,
		): Promise<unknown> => {
			let saveError: unknown;
			try {
				await addTemplateCallback(templateId);
			} catch (error) {
				saveError = error;
			}

			if (saveError !== undefined) {
				createAnalyticsEvent({
					eventType: 'track',
					action: 'failed',
					actionSubject: 'workStyleChoice',
					attributes: {
						selection,
						teamType,
						templateType,
						templateId,
						errorName: saveError instanceof Error ? saveError.name : typeof saveError,
					},
				}).fire();
			}

			return saveError;
		},
		[addTemplateCallback, createAnalyticsEvent, teamType],
	);

	const commitChoice = useCallback(
		async ({ option, metadata }: TemplateOption, selection: WorkStyleChoiceSelection) => {
			if (hasCommittedChoice.current) {
				return;
			}
			hasCommittedChoice.current = true;

			const { templateId, templateType, title } = option;
			const isUserChoice = selection === 'chosen';

			if (isUserChoice) {
				setIsSaving(true);
				ufoEventConfirmationPageTemplateSelectionContinue.start();
				ufoEventConfirmationPageTemplateSelectionContinue.setCustomData({
					teamType,
					template: templateType,
					templateId,
				});
			}

			createAnalyticsEvent({
				eventType: 'track',
				action: 'processed',
				actionSubject: 'workStyleChoice',
				attributes: {
					selection,
					teamType,
					templateType,
					templateId,
				},
			}).fire();

			window.history.replaceState(
				{},
				'',
				getCurrentUrlWithParams({
					templateId,
					templateChoice: templateType ?? '',
				}),
			);

			const pendingSave = saveTemplate(option, selection);

			if (isUserChoice) {
				const saveError = await pendingSave;
				if (saveError !== undefined) {
					ufoEventConfirmationPageTemplateSelectionContinue.failure({ error: saveError });
				} else {
					ufoEventConfirmationPageTemplateSelectionContinue.success();
				}
			}

			setTemplateMetadata(metadata);
			setTemplateChoice(templateType);
			setTemplateChoiceId(templateId);
			setTemplateChoiceName(title);

			nextScreen();
		},
		[
			createAnalyticsEvent,
			nextScreen,
			saveTemplate,
			setTemplateChoice,
			setTemplateChoiceId,
			setTemplateChoiceName,
			setTemplateMetadata,
			teamType,
		],
	);

	useLayoutEffect(() => {
		if (plan.type === 'skip') {
			void commitChoice(plan.option, 'skipped');
		}
	}, [plan, commitChoice]);

	const options = plan.type === 'choose' ? plan.options : [];
	const selectedOption =
		options.find(({ option }) => option.templateId === selectedTemplateId) ?? options[0];

	const handleContinue = useCallback(() => {
		if (!selectedOption || hasCommittedChoice.current) {
			return;
		}
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: TRACKING_EVENT_ACTION_SUBJECT_IDS.workStyleChoiceContinue,
			attributes: {
				teamType,
				templateType: selectedOption.option.templateType,
				templateId: selectedOption.option.templateId,
			},
		}).fire();
		void commitChoice(selectedOption, 'chosen');
	}, [commitChoice, createAnalyticsEvent, selectedOption, teamType]);

	const canGoBack = prevScreenExists?.() ?? false;

	const handleBack = useCallback(() => {
		if (hasCommittedChoice.current) {
			return;
		}
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: TRACKING_EVENT_ACTION_SUBJECT_IDS.workStyleChoiceBack,
			attributes: { teamType },
		}).fire();
		const url = new URL(window.location.href);
		url.searchParams.delete('team_type');
		window.history.replaceState({}, '', url.toString());
		goBackToUserSegmentation();
		prevScreen();
	}, [createAnalyticsEvent, goBackToUserSegmentation, prevScreen, teamType]);

	const handleSelect = useCallback(
		({ option }: TemplateOption) => {
			createAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: TRACKING_EVENT_ACTION_SUBJECT_IDS.workStyleChoiceCard,
				attributes: {
					teamType,
					templateType: option.templateType,
					templateId: option.templateId,
				},
			}).fire();
			setSelectedTemplateId(option.templateId);
		},
		[createAnalyticsEvent, teamType],
	);

	const handleCardKeyDown = useCallback(
		(event: KeyboardEvent<HTMLButtonElement>) => {
			const step = RADIO_KEY_STEPS[event.key];
			if (step === undefined || !selectedOption) {
				return;
			}
			event.preventDefault();
			const currentIndex = options.indexOf(selectedOption);
			const nextIndex = (currentIndex + step + options.length) % options.length;
			const nextOption = options[nextIndex];
			if (!nextOption) {
				return;
			}
			handleSelect(nextOption);
			event.currentTarget.parentElement
				?.querySelectorAll<HTMLElement>('[role="radio"]')
				[nextIndex]?.focus();
		},
		[handleSelect, options, selectedOption],
	);

	if (!selectedOption) {
		return <WorkStyleChoiceLoadingState />;
	}

	return (
		<DataFoundationsWrapper
			dataFoundationsEventOption="WorkStyleChoiceScreenViewed"
			attributes={dataFoundationsAttributes}
			waitForAttributes={profile.requestInProgress}
		>
			<DwellTimeWrapper
				eventData={{
					action: 'dwelled',
					actionSubject: 'screen',
					actionSubjectId: 'WorkStyleChoiceScreen',
					source: '/ondemand/signup/confirmation',
				}}
				initialPollInterval={5000}
				idleTimeout={120000}
				nextPollIntervalCallback={(interval) => interval + 5000}
			>
				<JiraOnboardingContainer
					header={intl.formatMessage(workStyleChoiceMessages.header)}
					subheader={intl.formatMessage(workStyleChoiceMessages.subheader)}
				>
					<div
						role="radiogroup"
						css={cardsStyles}
						aria-label={intl.formatMessage(workStyleChoiceMessages.cardsLabel)}
					>
						{options.map((templateOption) => (
							<WorkStyleChoiceCard
								key={templateOption.option.templateId}
								templateOption={templateOption}
								isSelected={templateOption.option.templateId === selectedOption.option.templateId}
								onSelect={handleSelect}
								onKeyDown={handleCardKeyDown}
							/>
						))}
					</div>
					<div css={continueStyles}>
						{canGoBack && (
							<div css={backButtonStyles}>
								<Button
									shouldFitContainer
									appearance="subtle"
									isDisabled={isSaving}
									onClick={handleBack}
								>
									<FormattedMessage {...workStyleChoiceMessages.back} />
								</Button>
							</div>
						)}
						<div css={continueButtonStyles}>
							<Button
								shouldFitContainer
								appearance="primary"
								isDisabled={isSaving}
								onClick={handleContinue}
							>
								<FormattedMessage {...workStyleChoiceMessages.continue} />
							</Button>
						</div>
					</div>
				</JiraOnboardingContainer>
			</DwellTimeWrapper>
		</DataFoundationsWrapper>
	);
};
